import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Ban, Coins, Hand, Loader2, Minus, Radio, X } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import AmbassadorBadge from "@/components/AmbassadorBadge";
import AvatarLightbox from "@/components/AvatarLightbox";
import VerifiedBadge from "@/components/VerifiedBadge";

const PILL_MARGIN = 16; // px inset from viewport edges when snapped
const CORNER_KEY = "pandax_nearby_pill_corner";
const CORNERS = ["tl", "tr", "bl", "br"];

/**
 * Modal-style card that previews a person's profile without leaving the
 * map. Shows distance, city, online/paying badges, bio, and quick actions
 * (view profile / nudge / join live).
 *
 * Foldable + draggable: users hit the minimize button to shrink the modal
 * into a floating pill. The pill can then be dragged and it snaps to the
 * nearest corner — location is persisted in localStorage so future
 * previews open in the same spot. (localStorage note: only stores the
 * string "tl"/"tr"/"bl"/"br" corner code — no PII / auth data.)
 */
export default function NearbyPreviewCard({
  person,
  onClose,
  onNudge,
  isNudging,
  isNudged,
  onBlock,
}) {
  const [minimized, setMinimized] = useState(false);
  const [avatarZoom, setAvatarZoom] = useState(false);
  const [corner, setCorner] = useState(() => {
    try {
      const saved = window.localStorage.getItem(CORNER_KEY);
      return CORNERS.includes(saved) ? saved : "br";
    } catch { return "br"; }
  });
  if (!person) return null;

  const persistCorner = (c) => {
    setCorner(c);
    try { window.localStorage.setItem(CORNER_KEY, c); } catch { /* ignore */ }
  };

  // --- Minimized pill: floats above the sheet, snaps to any corner on drag.
  if (minimized) {
    return (
      <DraggablePill
        corner={corner}
        onCornerChange={persistCorner}
        person={person}
        onExpand={() => setMinimized(false)}
        onDismiss={onClose}
      />
    );
  }

  return (
    <div
      className="absolute inset-0 z-[1300] flex items-end sm:items-center justify-center p-4 pb-24 sm:pb-4"
      onClick={() => setMinimized(true)}
      data-testid="nearby-preview"
    >
      <div className="absolute inset-0 bg-black/60" />
      <div
        className="relative w-full max-w-sm bg-zinc-950 border border-white/10 rounded-3xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="absolute top-3 right-3 z-10 flex items-center gap-1.5">
          <button
            onClick={(e) => { e.stopPropagation(); onBlock?.(person); }}
            data-testid="nearby-preview-block"
            title="Block this user"
            className="w-8 h-8 rounded-full bg-black/60 backdrop-blur-md border border-white/10 text-zinc-400 flex items-center justify-center hover:border-rose-500 hover:text-rose-400 transition"
          >
            <Ban size={14} />
          </button>
          <button
            onClick={() => setMinimized(true)}
            data-testid="nearby-preview-minimize"
            title="Move out the way"
            className="w-8 h-8 rounded-full bg-black/60 backdrop-blur-md border border-white/10 text-white flex items-center justify-center hover:border-rose-500 transition"
          >
            <Minus size={16} />
          </button>
          <button
            onClick={onClose}
            data-testid="nearby-preview-close"
            title="Close"
            className="w-8 h-8 rounded-full bg-black/60 backdrop-blur-md border border-white/10 text-white flex items-center justify-center hover:border-rose-500 transition"
          >
            <X size={16} />
          </button>
        </div>
        <div
          className="h-32 bg-gradient-to-br from-rose-900 via-black to-yellow-900 relative"
          style={{ backgroundImage: `url(${person.avatar_url})`, backgroundSize: "cover", filter: "blur(24px) brightness(0.6)" }}
        />
        <div className="px-6 -mt-14 pb-6">
          <div className="relative inline-block">
            <Avatar
              className="w-24 h-24 border-4 border-zinc-950 cursor-pointer hover:ring-2 hover:ring-rose-500 transition"
              onClick={(e) => { e.stopPropagation(); setAvatarZoom(true); }}
              data-testid="nearby-preview-avatar-zoom"
              title="Tap to view larger"
            >
              <AvatarImage src={person.avatar_url} />
              <AvatarFallback className="bg-rose-600 text-white text-2xl">
                {person.display_name?.[0]?.toUpperCase()}
              </AvatarFallback>
            </Avatar>
            {person.is_live && (
              <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 text-[9px] uppercase tracking-widest bg-rose-600 text-white rounded-full px-2 py-0.5">
                Live
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setAvatarZoom(true); }}
            data-testid="nearby-preview-name-zoom"
            className="mt-3 text-left focus:outline-none focus:ring-2 focus:ring-rose-500 rounded-sm block"
            title="Tap to view profile picture larger"
          >
            <div className="flex items-center gap-1.5 flex-wrap">
              <div className="font-display text-2xl text-white">{person.display_name}</div>
              <VerifiedBadge show={person.is_verified} />
              <AmbassadorBadge
                status={person.ambassador_status}
                tier={person.ambassador_tier}
              />
            </div>
            <div className="text-xs text-zinc-500">{person.display_name}</div>
          </button>
          <div className="mt-3 flex items-center flex-wrap gap-1.5 text-[10px] uppercase tracking-widest">
            {person.is_visitor && (
              <span
                data-testid="nearby-preview-visitor-chip"
                className="bg-sky-500/20 border border-sky-400/50 rounded-full px-2 py-1 text-sky-200"
                title={person.note || undefined}
              >
                ✈️ Visiting · {person.start_date} → {person.end_date}
              </span>
            )}
            <span className="bg-white/5 border border-white/10 rounded-full px-2 py-1 text-zinc-300">
              {person.distance_mi == null ? "LIVE now" : person.pin_type === "fuzzy" ? `~${person.distance_mi} mi` : person.distance_mi < 1 ? "< 1 mi" : `${person.distance_mi} mi`}
            </span>
            {person.pin_type === "fuzzy" && (
              <span data-testid="nearby-preview-approx-chip" className="bg-zinc-700/40 border border-dashed border-white/30 rounded-full px-2 py-1 text-zinc-300" title="Not checked in — shown at city level only">
                Approximate · city-level
              </span>
            )}
            {person.city && (
              <span className="bg-white/5 border border-white/10 rounded-full px-2 py-1 text-zinc-300">
                {person.city}
              </span>
            )}
            {person.online_now && (
              <span className="bg-emerald-500/15 border border-emerald-500/30 rounded-full px-2 py-1 text-emerald-300">
                Online now
              </span>
            )}
            {person.is_paying_customer && (
              <span className="bg-yellow-400/15 border border-yellow-400/30 rounded-full px-2 py-1 text-yellow-300 flex items-center gap-1">
                <Coins size={10} /> Paying
              </span>
            )}
          </div>
          {person.bio && (
            <p className="mt-3 text-sm text-zinc-400 leading-relaxed line-clamp-3">{person.bio}</p>
          )}
          <div className="mt-4 flex flex-wrap gap-2">
            <Link to={`/u/${person.id}`} className="flex-1 min-w-[140px]">
              <Button
                className="w-full btn-crimson rounded-full"
                data-testid="nearby-preview-profile"
              >
                View profile
              </Button>
            </Link>
            <Button
              onClick={() => onNudge(person)}
              disabled={isNudging || isNudged}
              data-testid="nearby-preview-nudge"
              className="flex-1 min-w-[140px] rounded-full bg-yellow-400 hover:bg-yellow-300 disabled:bg-yellow-400/40 text-black font-semibold"
            >
              {isNudging ? (
                <><Loader2 size={14} className="animate-spin mr-1.5" /> Nudging…</>
              ) : isNudged ? (
                <><Hand size={14} className="mr-1.5" /> Nudged</>
              ) : (
                <><Hand size={14} className="mr-1.5" /> Nudge · 1 coin</>
              )}
            </Button>
            {person.is_live && person.live_stream_id && (
              <Link to={`/live?s=${person.live_stream_id}`} className="flex-1 min-w-[140px]">
                <Button
                  className="w-full rounded-full bg-rose-600 hover:bg-rose-500 text-white"
                  data-testid="nearby-preview-live"
                >
                  <Radio size={14} className="mr-1.5" /> Join live
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>
      <AvatarLightbox
        open={avatarZoom}
        onClose={() => setAvatarZoom(false)}
        src={person.avatar_url}
        handle={person.handle}
      />
    </div>
  );
}

/** Snap-to-corner styles for the minimized pill. Positive insets from
 * `PILL_MARGIN` so nothing sits flush with the ingress bar / bottom sheet. */
const cornerClass = {
  tl: "top-20 left-4",
  tr: "top-20 right-4",
  bl: "bottom-28 left-4",
  br: "bottom-28 right-4",
};

/**
 * Floating avatar pill that opens the preview modal when tapped. Long-press
 * or drag anywhere on the pill and it will snap to whichever corner you
 * release it in. Corner preference persists across sessions.
 */
function DraggablePill({ corner, onCornerChange, person, onExpand, onDismiss }) {
  const ref = useRef(null);
  const dragState = useRef({ startX: 0, startY: 0, dragging: false, moved: false });
  const [ghost, setGhost] = useState(null); // {x, y} while dragging

  useEffect(() => {
    if (!ghost) return;
    const onMove = (e) => {
      const p = "touches" in e ? e.touches[0] : e;
      setGhost({ x: p.clientX, y: p.clientY });
      dragState.current.moved = true;
    };
    const onUp = (e) => {
      const p = "changedTouches" in e ? e.changedTouches[0] : e;
      const w = window.innerWidth, h = window.innerHeight;
      const right = p.clientX > w / 2;
      const bottom = p.clientY > h / 2;
      const nextCorner = `${bottom ? "b" : "t"}${right ? "r" : "l"}`;
      onCornerChange(nextCorner);
      setGhost(null);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchend", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchend", onUp);
    };
  }, [ghost, onCornerChange]);

  const onPointerDown = (e) => {
    const p = "touches" in e ? e.touches[0] : e;
    dragState.current = { startX: p.clientX, startY: p.clientY, dragging: true, moved: false };
    // Start dragging state — we render a translucent copy at the cursor.
    setGhost({ x: p.clientX, y: p.clientY });
  };

  const onClickPill = (e) => {
    // If the pointer moved > 4px between down + up, treat as drag, not click.
    if (dragState.current.moved) {
      dragState.current.moved = false;
      e.preventDefault();
      return;
    }
    onExpand();
  };

  const pill = (extraProps = {}, className = "") => (
    <div
      className={`flex items-center gap-2 bg-zinc-950/95 backdrop-blur-md border border-white/15 rounded-full pl-2 pr-4 py-2 shadow-2xl select-none ${className}`}
      {...extraProps}
    >
      <Avatar className="w-8 h-8 border border-white/10">
        <AvatarImage src={person.avatar_url} />
        <AvatarFallback className="bg-rose-600 text-white text-xs">
          {person.display_name?.[0]?.toUpperCase()}
        </AvatarFallback>
      </Avatar>
      <span className="text-sm text-white font-medium truncate max-w-[160px]">
        {person.display_name}
      </span>
      <span
        onClick={(e) => { e.stopPropagation(); onDismiss(); }}
        className="text-zinc-500 hover:text-rose-400 ml-1 cursor-pointer"
        role="button"
        aria-label="Dismiss preview"
        data-testid="nearby-preview-pill-close"
      >
        <X size={13} />
      </span>
    </div>
  );

  return (
    <>
      {/* Ghost that follows the pointer while dragging */}
      {ghost && (
        <div
          className="pointer-events-none fixed z-[1400] opacity-70"
          style={{ left: ghost.x - 24, top: ghost.y - 20 }}
          aria-hidden="true"
        >
          {pill({}, "ring-2 ring-rose-500")}
        </div>
      )}

      <div
        ref={ref}
        data-testid="nearby-preview-pill"
        title="Drag me to another corner · tap to reopen"
        className={`absolute z-[1250] cursor-grab active:cursor-grabbing hover:brightness-110 transition ${cornerClass[corner]}`}
        onMouseDown={onPointerDown}
        onTouchStart={onPointerDown}
        onClick={onClickPill}
      >
        {pill({})}
      </div>
    </>
  );
}

